import {JSX} from "lib/commons/xml/dom";
import {InfoFocusItem, IShortDescCtx, ITEM} from "lib/wsp/item";
import {JSrcIdent, SRC} from "lib/wsp/src";
import {ERROR} from "lib/core/errorReport";
import {IDatasLcSimpleTransition, ILcProvider, ITaskCreationCtx, ItemTypeTask} from "lib/wsp/wspMetaUi";
import {LIFECYCLE, TASK} from "lib/wsp/lcTask";
import {SrcAction} from "back/wsp/actions/srcActions";
import {ActionMenuDep, IAction} from "lib/commons/actions";
import {JLDTaskMain} from "back/wsp/views/itemMain";


/** Action de création d'une tâche. */
export class CreateTask<C extends ITaskCreationCtx> extends SrcAction<C> {

	constructor(public taskType: ItemTypeTask) {
		super(taskType.getModel());
		this._label = taskType.getTitle();
		this._icon = taskType.getIcon();
	}

	execute(ctx: C, ev?: Event) {
		if (ctx.infoBroker) {
			const datas: JLDTaskMain = {tkSt: this.taskType.getTaskInitStates(ctx)};
			ctx.infoBroker.dispatchInfo(new InfoFocusItem(SRC.createNewSrcUri(this._id), null, datas), null);
		}
	}
}

/**
 * Action d'exécution d'une transition
 */
export class SrcExecTransition<C extends IShortDescCtx> extends SrcAction<C> {

	constructor(public trans: IDatasLcSimpleTransition, public lcProv: ILcProvider, overrideExec?: (ctx: C, ev?: Event) => any) {
		super(trans.code);
		if (trans.showPerm) this.requireSrcVisiblePerm(trans.showPerm);
		if (trans.enablePerm) this.requireSrcEnabledPerm(trans.enablePerm);
		this._label = trans.name;
		this._icon = trans.iconUrl || lcProv.getLcState(trans.targetState).iconUrl;
		if (overrideExec) this.execute = overrideExec;
	}

	isVisible(ctx: C): boolean {
		if (this.trans.isTransitionVisible && this.trans.isTransitionVisible(ctx.shortDescs, ctx.reg, ctx.emitter) === false) return false;
		if (!super.isVisible(ctx)) return false;
		// On ne laisse visibles que les transitions actives sur au moins l'une des rows sélectionnées
		return this._checkAtLeastOne(ctx);
	}

	isEnabled(ctx: C): boolean {
		if (this.trans.isTransitionEnabled && this.trans.isTransitionEnabled(ctx.shortDescs, ctx.reg, ctx.emitter) === false) return false;
		if (!super.isEnabled(ctx)) return false;
		if (ctx.shortDescs.length > 1) {
			//Si plusieurs src sélectionnés, on laisse visible mais désactivé si la transition ne peut être activée sur une des sources.
			if (!this._checkAll(ctx)) return false;
		}
		return true;
	}

	getGroup(ctx: C): string {
		return this.trans.group || super.getGroup(ctx);
	}

	/** Retourne true si TOUTES les sources peuvent se voir affecter cette transition */
	_checkAll(ctx: C): boolean {
		for (const sd of ctx.shortDescs) if (!LIFECYCLE.isTransAvailable(this.trans, sd.lcSt, sd.itModel, this.lcProv)) return false;
		return true;
	}

	/** Retourne true si AU MOINS UNE source peut se voir affecter cette transition */
	_checkAtLeastOne(ctx: C): boolean {
		for (const sd of ctx.shortDescs) if (LIFECYCLE.isTransAvailable(this.trans, sd.lcSt, sd.itModel, this.lcProv)) return true;
		return false;
	}

	async execute(ctx: C, ev?: Event) {
		const srcRefs: JSrcIdent[] = [];
		for (const src of ctx.shortDescs) {
			if (TASK.isTask(src)) continue; //cf task.ts lorsque la source est une task.
			srcRefs.push(src);
		}
		if (srcRefs.length === 0) return;
		let params: { [srcUri: string]: { [key: string]: Jsonisable } };
		await ctx.reg.env.wsp.wspServer.wspsLive.saveItems(ctx.reg.env.wsp.code, srcRefs);
		const { paramsProvider } = this.trans;
		if (paramsProvider) {
			params = {} as any;
			if (!await paramsProvider.computeParams(params, ctx.shortDescs, ctx.reg, ctx.emitter)) return;
		}
		if (this.trans.controlBeforeExecute && !await this.trans.controlBeforeExecute(ctx.shortDescs, ctx.reg, ctx.emitter, params)) {
			if (paramsProvider?.rollbackTransition) await paramsProvider.rollbackTransition(params, ctx.shortDescs, ctx.reg);
			return;
		}
		const result = await ITEM.execItemTransition(ctx.reg.env.wsp, ctx.emitter, this.trans.code, srcRefs, params);
		if (!result.ok) {
			return ERROR.reportError({msg: "Une erreur est survenue durant le changement d'état.", cause: result.error, adminDetails: await result.text()})
		}
	}
}

/**
 * Menu des transitions exécutables des items.
 */
export class ItemListTransition<C extends IShortDescCtx> extends ActionMenuDep<C> {

	constructor() {
		super('itemListTransition');
		this._label = "Changer d'état...";
		this._group = "edit";
	}

	getActions(ctx: C): IAction<C>[] {
		const lcProv: ILcProvider & { _listTrActions?: IAction<C>[] } = ctx.reg.env.wsp.wspMetaUi;
		if (!lcProv._listTrActions) {
			const list: IAction<C>[] = lcProv._listTrActions = [];
			const transitions = lcProv.getLcTransitions();
			if (transitions != null) for (const tr of transitions.values()) list.push(new SrcExecTransition(tr, lcProv));
		}
		return lcProv._listTrActions;
	}
}